import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Lock, Flame, Sparkles } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * 🩸🔥 Awakenings Gallery — Dragonious RPG only.
 * Every Awakening = a hero's second face. Art + tagline + bleed motif +
 * kit bleed pillars. Canon file: /app/memory/AWAKENINGS_SYSTEM_CANON_FEB2026.md
 */
export default function AwakeningsGallery() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const { data } = await axios.get(`${API}/awakenings`);
        setItems(data.items || []);
      } catch {
        toast.error("Couldn't load Awakenings. Try again in a moment.");
      } finally {
        setLoading(false);
      }
    })();
  }, [user]);

  if (!user) {
    return (
      <div className="min-h-screen bg-black text-neutral-200 flex items-center justify-center px-6">
        <div className="max-w-md w-full border border-red-950/60 bg-[#0F0505] p-8 text-center">
          <Lock className="mx-auto mb-3 text-red-500" size={32} />
          <h1 className="font-serif text-3xl mb-2">Awakenings sealed.</h1>
          <p className="text-sm text-neutral-400 mb-6">
            Sign in to enter the Dragonious RPG canon. Awakenings are
            reserved for heroes who have already bled here.
          </p>
          <Link
            to="/#auth"
            data-testid="awakenings-signin-link"
            className="inline-block px-5 py-2 bg-red-600 hover:bg-red-500 text-white text-xs uppercase tracking-widest"
          >
            Sign in
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0A0004] via-[#050505] to-[#0A0004] text-neutral-200">
      {/* Header */}
      <header className="max-w-6xl mx-auto px-6 pt-16 pb-10 relative">
        <div className="absolute inset-0 opacity-30 pointer-events-none bg-[radial-gradient(circle_at_top,#7f1d1d33,transparent_60%)]" />
        <div className="relative">
          <div className="flex items-center gap-3 text-red-500/80 uppercase tracking-[0.35em] text-[10px] mb-3">
            <Flame size={12} />
            <span>Dragonious RPG · Canon Only</span>
          </div>
          <h1 className="font-serif text-5xl sm:text-6xl leading-none">
            <span className="text-neutral-100">AWA</span>
            <span className="text-red-500 drop-shadow-[0_0_18px_rgba(220,38,38,0.4)]">KEN</span>
            <span className="text-neutral-100">INGS</span>
          </h1>
          <p className="mt-4 max-w-2xl text-sm text-neutral-400 leading-relaxed">
            The mask cracks. The seal spills. The Script mis-reads. Every
            Awakening is a hero's second face — <em>the one they were
            never meant to show.</em> They live only here, in the RPG.
            Fighters may borrow the likeness. The bleed stays with us.
          </p>
        </div>
      </header>

      {/* Grid */}
      <main className="max-w-6xl mx-auto px-6 pb-24">
        {loading ? (
          <div className="flex items-center gap-3 text-neutral-500 text-sm py-16">
            <Loader2 className="animate-spin" size={16} /> Reading the canon…
          </div>
        ) : items.length === 0 ? (
          <p className="text-neutral-500 text-sm italic">
            No Awakenings scribed yet. The vault is quiet.
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="awakenings-grid">
            {items.map((a) => (
              <AwakeningCard key={a.hero_id} awakening={a} onOpen={() => setSelected(a)} />
            ))}
          </div>
        )}
      </main>

      {selected && (
        <AwakeningModal awakening={selected} onClose={() => setSelected(null)} />
      )}
    </div>
  );
}

function AwakeningCard({ awakening, onOpen }) {
  const a = awakening;
  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid={`awakening-card-${a.hero_id}`}
      className="group relative text-left border border-zinc-900 hover:border-[color:var(--accent)] bg-black overflow-hidden transition-all"
      style={{ "--accent": a.accent }}
    >
      <div className="relative aspect-[3/4] overflow-hidden">
        <img
          src={a.art_url}
          alt={a.title}
          loading="lazy"
          className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
        />
        {/* Bleed vignette */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: `linear-gradient(180deg, transparent 40%, ${a.accent}22 75%, #000 100%)`,
          }}
        />
        {/* Fighters cross-canon badge */}
        {a.fighters_counterpart && (
          <span
            className="absolute top-3 left-3 text-[9px] uppercase tracking-widest px-2 py-1 bg-black/70 border"
            style={{ color: a.accent, borderColor: `${a.accent}88` }}
          >
            Bleeds from Fighters
          </span>
        )}
        {/* Title floated on the art */}
        <div className="absolute bottom-0 inset-x-0 p-4">
          <div className="text-[10px] uppercase tracking-widest text-neutral-400 mb-1">
            {a.subtitle}
          </div>
          <h3 className="font-serif text-xl leading-tight text-white drop-shadow-[0_2px_6px_rgba(0,0,0,0.9)]">
            {a.title}
          </h3>
        </div>
      </div>
      <div className="p-4 space-y-2 border-t border-zinc-900">
        <p className="text-sm italic text-neutral-300 leading-snug">
          "{a.tagline}"
        </p>
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-neutral-500">
          <Sparkles size={10} style={{ color: a.accent }} />
          <span>{a.bleed_motif}</span>
        </div>
        {a.unlock_trigger?.descriptor && (
          <div
            className="text-[10px] uppercase tracking-widest pt-2 border-t border-zinc-900/60 mt-2 flex gap-2 items-center"
            style={{ color: a.accent }}
          >
            <Lock size={10} />
            <span className="italic normal-case tracking-normal text-[11px] text-neutral-400">{a.unlock_trigger.descriptor}</span>
          </div>
        )}
      </div>
    </button>
  );
}

function AwakeningModal({ awakening, onClose }) {
  const a = awakening;
  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center px-4 py-8 overflow-y-auto"
      onClick={onClose}
      data-testid={`awakening-modal-${a.hero_id}`}
    >
      <div
        className="max-w-4xl w-full bg-[#0A0505] border border-zinc-800 shadow-2xl relative"
        onClick={(e) => e.stopPropagation()}
        style={{ boxShadow: `0 0 40px ${a.accent}33` }}
      >
        <button
          type="button"
          onClick={onClose}
          data-testid="awakening-modal-close"
          className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center bg-black/60 hover:bg-black text-neutral-300 hover:text-white text-lg z-10"
        >
          ×
        </button>
        <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)]">
          <div className="relative aspect-[3/4] md:aspect-auto md:min-h-[520px] bg-black overflow-hidden">
            <img
              src={a.art_url}
              alt={a.title}
              className="w-full h-full object-cover"
            />
          </div>
          <div className="p-6 md:p-8 space-y-5">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">
                {a.subtitle}
              </div>
              <h2 className="font-serif text-3xl leading-tight" style={{ color: a.accent }}>
                {a.title}
              </h2>
              {a.fighters_counterpart && (
                <div className="mt-2 text-[10px] uppercase tracking-widest text-neutral-500">
                  Cross-canon bleed: Fighters &raquo; <span className="text-neutral-300">{a.fighters_counterpart}</span>
                </div>
              )}
            </div>
            <blockquote
              className="border-l-2 pl-4 italic text-neutral-300 text-base leading-relaxed"
              style={{ borderColor: a.accent }}
            >
              "{a.tagline}"
            </blockquote>
            <p className="text-sm text-neutral-400 leading-relaxed">
              {a.long_desc}
            </p>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">
                Bleed motif
              </div>
              <p className="text-sm text-neutral-300">{a.bleed_motif}</p>
            </div>
            {a.kit_bleed?.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">
                  Kit bleed
                </div>
                <ul className="space-y-2 text-sm text-neutral-300">
                  {a.kit_bleed.map((line, i) => (
                    <li key={i} className="flex gap-2">
                      <span style={{ color: a.accent }}>▸</span>
                      <span>{line}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {a.unlock_trigger?.descriptor && (
              <div className="pt-4 border-t border-zinc-900">
                <div className="flex items-start gap-3">
                  <Lock size={16} className="mt-1" style={{ color: a.accent }} />
                  <p className="text-sm italic text-neutral-400 leading-relaxed">
                    {a.unlock_trigger.descriptor}
                  </p>
                </div>
              </div>
            )}
            {a.cosmic_weapon_dormant_art_url && (
              <div
                data-testid={`awakening-cosmic-weapon-${a.hero_id}`}
                className="pt-4 border-t border-zinc-900"
              >
                <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">
                  Cosmic weapon · dormant
                </div>
                <div className="flex gap-3 items-start">
                  <img
                    src={a.cosmic_weapon_dormant_art_url}
                    alt={a.cosmic_weapon_name || "Cosmic weapon"}
                    loading="lazy"
                    className="w-24 h-24 object-cover border border-zinc-800 shrink-0"
                    style={{ boxShadow: `0 0 18px ${a.accent}44` }}
                  />
                  <div className="min-w-0">
                    {a.cosmic_weapon_name && (
                      <div className="font-serif text-base text-neutral-100 leading-tight">
                        {a.cosmic_weapon_name}
                      </div>
                    )}
                    {a.cosmic_weapon_note && (
                      <p className="text-xs text-neutral-400 mt-1 leading-relaxed">
                        {a.cosmic_weapon_note}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
